import { Component, Element, Event, h, Host, Listen, Method, Prop, State, Watch, } from "@stencil/core";
import { outsideClickHandler } from "../utils/outside-click";
import { position } from "../utils/position";
export class Modal {
  constructor() {
    this.autoclose = false;
    this.backdrop = false;
    this.containerstyle = 'info';
    this.flash = false;
    this.version = '2.8.0';
    this.closeOnOutsideClick = true; // whether to show modal on initial load
    // set to true as soon as positioning is finished
    this.positioned = false;
    this.slottedContent = null;
    this.flashContent = null;
    this.closeable = false;
  }
  async toggle(target) {
    if (target) {
      this.toggleEl = target;
    }
    else if (this.slottedToggle) {
      this.toggleEl = this.slottedToggle;
    }
    else {
      this.toggleEl = document.activeElement;
    }
    this.closeable = this.slottedContent.getAttribute("closeable") === '';
    this.visible = !this.visible;
  }
  stateChangeHandle(visible) {
    if (visible) {
      window.setTimeout(() => {
        this.openModal();
      }, 30);
    }
    else {
      this.closeModal();
    }
  }
  async openModal() {
    position(this);
    // focus
    window.setTimeout(() => {
      this.modalContentEl.focus();
    }, 300);
    this.outsideClickHandler = outsideClickHandler(this.el, this.handleOutsideClick.bind(this));
    if (this.autoclose) {
      window.setTimeout(() => {
        this.visible = false;
      }, 3000);
    }
  }
  async closeModal() {
    if (this.outsideClickHandler) {
      this.outsideClickHandler.destroy();
    }
    if (this.popperInstance) {
      this.popperInstance.destroy();
    }
    this.positioned = false;
    this.close.emit();
  }
  handleKeyDown(ev) {
    // if enter or spacebar is pressed on this element, we delegate it to click
    if (ev.keyCode === 27) {
      if (this.slottedToggle) {
        this.slottedToggle.click();
      }
      else {
        this.visible = false;
      }
    }
  }
  handleOutsideClick() {
    if (!this.backdrop && this.closeOnOutsideClick) {
      window.setTimeout(() => {
        this.visible = false;
        if (this.slottedToggle) {
          this.slottedToggle.state = 1;
        }
        else if (this.toggleEl && this.toggleEl.tagName === 'C-TOGGLE') {
          this.toggleEl.state = 1;
        }
      }, 50);
    }
  }
  componentWillLoad() {
    // check if there is a toggle Element given via slot[name=overlay] (Mandatory)
    this.slottedContent = this.flash ? this.flashContent : this.el.querySelector('[slot="content"]');
    if (!this.slottedContent) {
      console.warn('please provide an element with slot="content"');
      return;
    }
    // check if there is a toggle Element given via slot[name=toggle] (Optional)
    this.slottedToggle = this.el.querySelector('[slot="toggle"]');
    if (this.slottedToggle) {
      this.toggleEl = this.slottedToggle;
      this.slottedToggle.addEventListener("click", () => {
        this.visible = !this.visible;
      });
    }
    this.slottedContent.addEventListener("close", () => {
      this.visible = false;
    });
  }
  componentDidLoad() {
    if (this.flash) {
      this.slottedContent = this.flashContent;
    }
    if (this.visible) {
      this.openModal();
    }
  }
  componentWillUnload() {
    if (this.popperInstance) {
      this.popperInstance.destroy();
    }
  }
  render() {
    const attributes = {};
    if (this.containerstyle) {
      attributes[this.containerstyle] = this.containerstyle;
    }
    return (h(Host, null,
      this.slottedToggle && (h("div", { 
        //   onClick={() => this.toggle()}
        class: {
          toggle: true,
          active: this.visible,
        } },
        h("slot", { name: "toggle" }))),
      !this.flash && (h("div", { ref: (el) => (this.modalContentEl = el), class: {
          modal: true,
          fade: true,
          show: this.visible,
          positioned: this.positioned
        }, tabindex: "0", role: "dialog", "aria-modal": "true" }, this.slottedContent && h("slot", { name: "content" }))),
      this.flash && (h("div", { ref: (el) => (this.modalContentEl = el), class: {
          modal: true,
          fade: true,
          show: this.visible,
          positioned: this.positioned
        }, tabindex: "0", role: "dialog", "aria-modal": "true" },
        h("c-container", Object.assign({ ref: (el) => (this.flashContent = el) }, attributes, { "full-width": this.position !== "left", fullHeight: this.position === "left", "p-2": true }),
          h("c-button", { primary: true, alternate: true, class: "control", onClick: () => { this.visible = false; } },
            h("span", null, "OK")),
          this.slottedContent && h("slot", { name: "content" })))),
      this.visible && this.backdrop && (h("div", { class: "backdrop", onClick: () => {
          if (this.closeable) {
            this.toggle();
          }
        } }))));
  }
  static get is() { return "c-modal"; }
  static get encapsulation() { return "shadow"; }
  static get originalStyleUrls() { return {
    "$": ["c-modal.scss"]
  }; }
  static get styleUrls() { return {
    "$": ["c-modal.css"]
  }; }
  static get properties() { return {
    "autoclose": {
      "type": "boolean",
      "mutable": false,
      "complexType": {
        "original": "boolean",
        "resolved": "boolean",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": ""
      },
      "attribute": "autoclose",
      "reflect": true,
      "defaultValue": "false"
    },
    "backdrop": {
      "type": "boolean",
      "mutable": false,
      "complexType": {
        "original": "boolean",
        "resolved": "boolean",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": ""
      },
      "attribute": "backdrop",
      "reflect": true,
      "defaultValue": "false"
    },
    "containerstyle": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "string",
        "resolved": "string",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": ""
      },
      "attribute": "containerstyle",
      "reflect": true,
      "defaultValue": "'info'"
    },
    "flash": {
      "type": "boolean",
      "mutable": false,
      "complexType": {
        "original": "boolean",
        "resolved": "boolean",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": ""
      },
      "attribute": "flash",
      "reflect": true,
      "defaultValue": "false"
    },
    "position": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "'bottom' | 'left' | 'right'",
        "resolved": "\"bottom\" | \"left\" | \"right\"",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": ""
      },
      "attribute": "position",
      "reflect": true
    },
    "version": {
      "type": "string",
      "mutable": true,
      "complexType": {
        "original": "string",
        "resolved": "string",
        "references": {}
      },
      "required": false,
      "optional": true,
      "docs": {
        "tags": [],
        "text": ""
      },
      "attribute": "version",
      "reflect": true,
      "defaultValue": "'2.8.0'"
    },
    "visible": {
      "type": "boolean",
      "mutable": true,
      "complexType": {
        "original": "boolean",
        "resolved": "boolean",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": ""
      },
      "attribute": "visible",
      "reflect": true
    },
    "closeOnOutsideClick": {
      "type": "boolean",
      "mutable": false,
      "complexType": {
        "original": "boolean",
        "resolved": "boolean",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": ""
      },
      "attribute": "close-on-outside-click",
      "reflect": false,
      "defaultValue": "true"
    }
  }; }
  static get states() { return {
    "positioned": {}
  }; }
  static get events() { return [{
      "method": "close",
      "name": "close",
      "bubbles": true,
      "cancelable": true,
      "composed": true,
      "docs": {
        "tags": [],
        "text": ""
      },
      "complexType": {
        "original": "any",
        "resolved": "any",
        "references": {}
      }
    }]; }
  static get methods() { return {
    "toggle": {
      "complexType": {
        "signature": "(target?: any) => Promise<void>",
        "parameters": [{
            "tags": [],
            "text": ""
          }],
        "references": {
          "Promise": {
            "location": "global"
          },
          "HTMLElement": {
            "location": "global"
          }
        },
        "return": "Promise<void>"
      },
      "docs": {
        "text": "",
        "tags": []
      }
    },
    "openModal": {
      "complexType": {
        "signature": "() => Promise<void>",
        "parameters": [],
        "references": {
          "Promise": {
            "location": "global"
          }
        },
        "return": "Promise<void>"
      },
      "docs": {
        "text": "",
        "tags": []
      }
    },
    "closeModal": {
      "complexType": {
        "signature": "() => Promise<void>",
        "parameters": [],
        "references": {
          "Promise": {
            "location": "global"
          }
        },
        "return": "Promise<void>"
      },
      "docs": {
        "text": "",
        "tags": []
      }
    }
  }; }
  static get elementRef() { return "el"; }
  static get watchers() { return [{
      "propName": "visible",
      "methodName": "stateChangeHandle"
    }]; }
  static get listeners() { return [{
      "name": "keydown",
      "method": "handleKeyDown",
      "target": undefined,
      "capture": false,
      "passive": false
    }]; }
}
